import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import {
  Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning,
} from "lucide-react";
import { fileUrl } from "@/lib/api";

export { STATIC_IMG } from "@/lib/staticImages";

export const CHANGES = "verkleinert und zugeschnitten";
export const uploadCredit = (title, author) => ({
  title, author: author || "Name nicht angegeben", source: "Einreichung bei WattNu", source_url: null,
  license: "Nutzungsrecht laut Nutzungsbedingungen", license_url: "/nutzungsbedingungen", changes: CHANGES,
});

export const PLACE_CAT = {
  strand: { label: "Strände", color: "#C9A56A" },
  wattwandern: { label: "Wattwandern", color: "#6F7F72" },
  radtouren: { label: "Radtouren", color: "#7FA084" },
  museen: { label: "Museen", color: "#102B3F" },
  gastronomie: { label: "Gastronomie", color: "#C65842" },
  sehenswuerdigkeiten: { label: "Sehenswürdigkeiten", color: "#9B5D2E" },
  natur: { label: "Natur & Tiere", color: "#3F5E45" },
};

export const EVENT_CAT = {
  musik: { label: "Musik & Konzert" },
  markt: { label: "Markt" },
  fuehrung: { label: "Führung & Watt" },
  sport: { label: "Sport & Rad" },
  kultur: { label: "Kultur & Kunst" },
  familie: { label: "Familie & Kinder" },
  fest: { label: "Fest & Feier" },
};

const resized = (url, w) =>
  url.includes("dam.destination.one")
    ? `https://img.ecmaps.de/remote/.jpg?url=${encodeURIComponent(url)}&scale=both&mode=crop&quality=80&width=${w}&height=${Math.round(w * 0.5625)}`
    : url;

export const eventImage = (ev, w = 1200) =>
  ev.image_path ? fileUrl(ev.image_path) : ev.image_url ? resized(ev.image_url, w) : null;
export const eventCredit = (ev) => (!ev.image_path && !ev.image_url ? null : ev.photo_credit || (ev.image_path ? uploadCredit(ev.title, ev.organizer) : ev.image_credit));

export const thumb = (url, w = 500) => (url || "").replace(/\/1280px-/, `/${w}px-`) || null;

export const placeImage = (p) => (p.image_path ? fileUrl(p.image_path) : p.image || null);
export const placeCredit = (p) => (p.image_path ? p.photo_credit || uploadCredit(p.name) : p.image ? p.photo_credit || p.image_credit : null);
export const isSponsored = (ev) => !!ev.sponsored || (!!ev.promoted_until && new Date(ev.promoted_until) > new Date());

export const fmt = (s, pattern) => (s ? format(parseISO(s), pattern, { locale: de }) : "");
export const fmtDay = (s) => fmt(s, "EEEE, d. MMMM yyyy");
export const fmtTime = (s) => fmt(s, "HH:mm");
export const fmtRange = (ev) => {
  const sameDay = ev.end && ev.end.slice(0, 10) === ev.start.slice(0, 10);
  if (!ev.end) return `${fmt(ev.start, "EEE, d. MMM")} · ${fmtTime(ev.start)} Uhr`;
  if (sameDay) return `${fmt(ev.start, "EEE, d. MMM")} · ${fmtTime(ev.start)}–${fmtTime(ev.end)} Uhr`;
  return `${fmt(ev.start, "d. MMM HH:mm")} – ${fmt(ev.end, "d. MMM HH:mm")}`;
};

export function weatherInfo(code) {
  if (code === 0) return { label: "Klar", Icon: Sun };
  if (code <= 2) return { label: code === 1 ? "Überwiegend klar" : "Teils bewölkt", Icon: CloudSun };
  if (code === 3) return { label: "Bedeckt", Icon: Cloud };
  if (code <= 48) return { label: "Nebel", Icon: CloudFog };
  if (code <= 57) return { label: "Nieselregen", Icon: CloudDrizzle };
  if (code <= 67) return { label: "Regen", Icon: CloudRain };
  if (code <= 77) return { label: "Schnee", Icon: CloudSnow };
  if (code <= 82) return { label: "Schauer", Icon: CloudRain };
  if (code <= 86) return { label: "Schneeschauer", Icon: CloudSnow };
  return { label: "Gewitter", Icon: CloudLightning };
}

const DIRS = ["N", "NO", "O", "SO", "S", "SW", "W", "NW"];
export const windDir = (deg) => DIRS[Math.round(((deg ?? 0) % 360) / 45) % 8];
const BFT = [1, 6, 12, 20, 29, 39, 50, 62, 75, 89, 103, 118];
export const beaufort = (kmh) => {
  const i = BFT.findIndex((t) => kmh < t);
  return i === -1 ? 12 : i;
};

const ISLANDS = ["Borkum", "Juist", "Norderney", "Baltrum", "Langeoog", "Spiekeroog", "Wangerooge"];
// "auf Norderney", but "in Norden"
export const atPlace = (name) => `${ISLANDS.includes((name || "").trim()) ? "auf" : "in"} ${name}`;
